import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  CheckCircle2,
  Circle,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}
const isoDate = (d) => {
  const x = new Date(d);
  x.setHours(12, 0, 0, 0);
  return x.toISOString().slice(0, 10);
};

export default function WeekViewPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation("app");
  const DAYS = t("week.days", { returnObjects: true });
  const dateLocale = (i18n.language || "it").startsWith("en") ? "en-GB" : "it-IT";
  const [searchParams, setSearchParams] = useSearchParams();
  const initialIso = searchParams.get("week");
  const [weekStart, setWeekStart] = useState(
    initialIso ? startOfWeek(new Date(initialIso)) : startOfWeek(new Date())
  );
  const [rapportini, setRapportini] = useState([]);
  const [loading, setLoading] = useState(true);

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const weekEnd = days[6];

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        date_from: isoDate(weekStart),
        date_to: isoDate(weekEnd),
      };
      const { data } = await api.get("/rapportini", { params });
      setRapportini(data || []);
    } catch (err) {
      console.error("[WeekView] load failed:", err?.message);
      toast.error(t("week.loadErr"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearchParams({ week: isoDate(weekStart) }, { replace: true });
    load();
  }, [weekStart]);

  const byDay = useMemo(() => {
    const map = {};
    for (const r of rapportini) {
      // Multi-rapportino support: ARRAY per giorno (cantieri diversi nello
      // stesso giorno). FIX (iter 30h): non si sovrascrivono più tra loro.
      const giorni = r.giorni && r.giorni.length ? r.giorni : null;
      if (giorni) {
        for (const g of giorni) {
          if (!g.data) continue;
          (map[g.data] = map[g.data] || []).push(r);
        }
      } else if (r.data) {
        (map[r.data] = map[r.data] || []).push(r);
      }
    }
    return map;
  }, [rapportini]);

  const goPrev = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };
  const goNext = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };

  const handleCreateOrOpen = async (date) => {
    const iso = isoDate(date);
    const rappList = byDay[iso] || [];
    // 1 rapportino → apri direttamente
    if (rappList.length === 1) {
      navigate(`/rapportino/${rappList[0].id}`);
      return;
    }
    // 2+ rapportini → vai alla lista filtrata per quella data (vede tutti)
    if (rappList.length >= 2) {
      navigate(`/lista?date_from=${iso}&date_to=${iso}`);
      return;
    }
    // 0 rapportini → crea nuovo
    if (user?.role === "viewer") {
      toast.info(t("week.viewerMsg"));
      return;
    }
    navigate(`/rapportino/new?data=${iso}`);
  };

  // Crea sempre un nuovo rapportino per questa data (anche se ne esistono già).
  const handleAddNew = (date) => {
    const iso = isoDate(date);
    if (user?.role === "viewer") return;
    navigate(`/rapportino/new?data=${iso}`);
  };

  // Compute totals across the week (avoid double-counting a multi-day rapportino)
  const seenIds = new Set();
  let totaleSettimana = 0;
  for (const r of rapportini) {
    if (seenIds.has(r.id)) continue;
    seenIds.add(r.id);
    const giorni = r.giorni && r.giorni.length ? r.giorni : null;
    if (giorni) {
      for (const g of giorni) {
        for (const rg of (g.righe || [])) totaleSettimana += Number(rg.ore_tot || 0);
      }
    } else {
      for (const rg of (r.righe || [])) totaleSettimana += Number(rg.ore_tot || 0);
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            {t("week.kicker")}
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
            {t("week.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t("week.from")}{" "}
            <span className="font-semibold text-slate-700">
              {weekStart.toLocaleDateString(dateLocale)}
            </span>{" "}
            {t("week.to")}{" "}
            <span className="font-semibold text-slate-700">
              {weekEnd.toLocaleDateString(dateLocale)}
            </span>{" "}
            · {rapportini.length} {t("week.reports", { count: rapportini.length })} · {totaleSettimana.toFixed(1)} {t("week.hours")}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Link to="/scansiona">
            <Button
              variant="outline"
              className="h-12 border-[#0044FF] text-[#0044FF] hover:bg-[#0044FF] hover:text-white"
              data-testid="scan-rapportino-button"
            >
              <Sparkles className="h-4 w-4 mr-2" /> {t("week.scanAI")}
            </Button>
          </Link>
          <Button
            variant="outline"
            size="icon"
            className="h-12 w-12"
            onClick={goPrev}
            data-testid="week-prev-button"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            className="h-12"
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            data-testid="week-current-button"
          >
            <Calendar className="h-4 w-4 mr-2" /> {t("week.thisWeek")}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-12 w-12"
            onClick={goNext}
            data-testid="week-next-button"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-500">{t("week.loading")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
          {days.map((d, idx) => {
            const iso = isoDate(d);
            const rappList = byDay[iso] || [];
            const count = rappList.length;
            // Aggregati di TUTTI i rapportini del giorno (cantieri diversi)
            let ore = 0;
            let righeCount = 0;
            const commesseSet = new Set();
            for (const r of rappList) {
              const giorni = r.giorni && r.giorni.length ? r.giorni : null;
              const day = giorni ? giorni.find((g) => g.data === iso) : null;
              const righe = day ? day.righe || [] : (r.righe || []);
              for (const rg of righe) {
                ore += Number(rg.ore_tot || 0);
                if (rg.commessa_codice) commesseSet.add(rg.commessa_codice);
              }
              righeCount += righe.length;
            }
            const commesseList = Array.from(commesseSet);
            const today = iso === isoDate(new Date());
            const hasRapp = count > 0;
            return (
              <Card
                key={iso}
                onClick={() => handleCreateOrOpen(d)}
                data-testid={`week-day-${idx}`}
                className={`p-4 cursor-pointer transition-all hover:border-[#0044FF] hover:shadow-md ${
                  today ? "border-[#0044FF] border-2" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">
                      {DAYS[idx]}
                    </div>
                    <div className="font-heading font-black text-2xl tracking-tight">
                      {d.getDate()}
                      <span className="text-xs font-normal text-slate-400">
                        /{String(d.getMonth() + 1).padStart(2, "0")}
                      </span>
                    </div>
                  </div>
                  {hasRapp ? (
                    <div className="flex items-center gap-1">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                      {count > 1 && (
                        <span
                          className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-[#0044FF] text-white"
                          data-testid={`day-count-${idx}`}
                          title={`${count} ${t("week.countTitle")}`}
                        >
                          ×{count}
                        </span>
                      )}
                    </div>
                  ) : (
                    <Circle className="h-6 w-6 text-slate-300" />
                  )}
                </div>

                <div className="mt-3 min-h-[60px]">
                  {hasRapp ? (
                    <>
                      <div className="text-[10px] uppercase font-bold text-slate-500">
                        {count === 1
                          ? `${t("week.rappN")}${rappList[0].numero}`
                          : `${count} ${t("week.reports")}`}
                      </div>
                      <div className="text-base font-heading font-bold mt-0.5">
                        {ore.toFixed(2)} {t("week.hours")}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {righeCount} {t("week.rows", { count: righeCount })} ·{" "}
                        {commesseList.length > 0
                          ? commesseList.slice(0, 3).join(", ") +
                            (commesseList.length > 3
                              ? ` +${commesseList.length - 3}`
                              : "")
                          : "—"}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-slate-400 mt-2">
                      {t("week.none")}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1.5 mt-3">
                  <Button
                    variant={hasRapp ? "outline" : "default"}
                    className={`flex-1 h-10 ${
                      !hasRapp ? "bg-[#0044FF] hover:bg-[#0033CC]" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateOrOpen(d);
                    }}
                    data-testid={`week-day-action-${idx}`}
                  >
                    {hasRapp ? (
                      <>
                        {count === 1 ? t("week.open") : `${t("week.see")} ${count}`}{" "}
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-1" /> {t("week.fill")}
                      </>
                    )}
                  </Button>
                  {hasRapp && user?.role !== "viewer" && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddNew(d);
                      }}
                      data-testid={`week-day-add-${idx}`}
                      title={t("week.addAnother")}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Card className="p-4 bg-slate-50 border-dashed">
        <div className="flex items-center justify-between flex-col md:flex-row gap-3">
          <div>
            <div className="font-heading font-bold">{t("week.fullList")}</div>
            <p className="text-xs text-slate-500">
              {t("week.fullListSub")}
            </p>
          </div>
          <Link to="/lista">
            <Button variant="outline" className="h-11">
              {t("week.goToList")} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
